import React, { useEffect, useMemo, useState } from "react";
import { apiGet, formatEuro } from "../lib/api";
import { PIZZERIA_MENU_STRUCTURE, sortPizzeriaMenuItems } from "../lib/pizzeria-menu-schema";
import MenuStaticContent from "./MenuStaticContent";
import "./Menu.css";

const VALID_SECTION_KEYS = new Set(PIZZERIA_MENU_STRUCTURE.map((section) => section.section_key));
const SECTION_TITLE_BY_KEY = Object.fromEntries(
  PIZZERIA_MENU_STRUCTURE.map((section) => [section.section_key, section.title])
);
const WHATSAPP_PHONE_NUMBER = "+393519520000";
const MAX_QTY_PER_VARIANT = 20;

function normalizePrice(rawPrice) {
  if (!rawPrice || typeof rawPrice !== "object" || Array.isArray(rawPrice)) {
    return null;
  }

  const source = rawPrice;
  const label = typeof source.label === "string" ? source.label.trim() : "";
  const amount = Number(source.amount_cents);
  if (!label || !Number.isInteger(amount) || amount < 1 || amount > 1_000_000) {
    return null;
  }

  return {
    label,
    amount_cents: amount
  };
}

function normalizeMenuItem(rawItem) {
  if (!rawItem || typeof rawItem !== "object" || Array.isArray(rawItem)) {
    return null;
  }

  const source = rawItem;
  const sectionKey = typeof source.section_key === "string" ? source.section_key.trim().toUpperCase() : "";
  if (!VALID_SECTION_KEYS.has(sectionKey)) {
    return null;
  }

  const name = typeof source.name === "string" ? source.name.trim() : "";
  if (!name) {
    return null;
  }

  const prices = Array.isArray(source.prices) ? source.prices.map(normalizePrice).filter(Boolean) : [];
  if (prices.length < 1 || prices.length > 2) {
    return null;
  }

  return {
    id: typeof source.id === "string" ? source.id : "",
    section_key: sectionKey,
    subsection_key:
      typeof source.subsection_key === "string" && source.subsection_key.trim()
        ? source.subsection_key.trim().toUpperCase()
        : undefined,
    name,
    description: typeof source.description === "string" ? source.description.trim() : "",
    prices,
    sort_order: Number.isInteger(Number(source.sort_order)) ? Number(source.sort_order) : 100,
    active: source.active !== false
  };
}

function resolvePriceHeaders(items, sectionConfig) {
  const defaults = Array.isArray(sectionConfig.default_price_labels) ? sectionConfig.default_price_labels : [];
  const hasDualPrices = items.some((item) => item.prices.length > 1);

  if (hasDualPrices) {
    const firstLabel = items.find((item) => item.prices[0]?.label)?.prices[0]?.label || defaults[0] || "Prezzo 1";
    const secondLabel = items.find((item) => item.prices[1]?.label)?.prices[1]?.label || defaults[1] || "Prezzo 2";
    return {
      hasDualPrices,
      thirdHeader: firstLabel,
      fourthHeader: secondLabel
    };
  }

  const singleLabel = items.find((item) => item.prices[0]?.label)?.prices[0]?.label || defaults[0] || "Prezzo";
  return {
    hasDualPrices,
    thirdHeader: "",
    fourthHeader: singleLabel
  };
}

function sanitizeQty(value) {
  const number = Number.parseInt(String(value), 10);
  if (!Number.isFinite(number) || number <= 0) {
    return 0;
  }

  return Math.min(number, MAX_QTY_PER_VARIANT);
}

function normalizePhoneNumber(rawValue) {
  return String(rawValue || "").replace(/\D/g, "");
}

function getMenuItemKey(item) {
  const id = typeof item?.id === "string" ? item.id.trim() : "";
  if (id) {
    return id;
  }

  return `${item?.section_key || "SECTION"}-${item?.subsection_key || "SUB"}-${item?.name || "ITEM"}`;
}

function getVariantKey(item, priceIndex) {
  return `${getMenuItemKey(item)}::${priceIndex}`;
}

function getVariantName(item, price) {
  const label = typeof price?.label === "string" ? price.label.trim() : "";
  const hasMultiplePrices = Array.isArray(item?.prices) && item.prices.length > 1;
  return hasMultiplePrices && label ? `${item.name} (${label})` : item.name;
}

function buildWhatsappMessage(selectedEntries) {
  const lines = ["Ciao! Vorrei ordinare dal menu:"];
  let currentSection = "";

  selectedEntries.forEach((entry) => {
    if (currentSection !== entry.item.section_key) {
      currentSection = entry.item.section_key;
      lines.push("");
      lines.push(`${SECTION_TITLE_BY_KEY[currentSection] || currentSection}:`);
    }

    lines.push(`- ${getVariantName(entry.item, entry.price)} x${entry.qty}`);
  });

  const totalQty = selectedEntries.reduce((total, entry) => total + entry.qty, 0);
  const totalAmountCents = selectedEntries.reduce((total, entry) => total + entry.line_total_cents, 0);

  lines.push("");
  lines.push(`Totale pezzi: ${totalQty}`);
  lines.push(`Totale stimato: ${formatEuro(totalAmountCents)}`);
  lines.push("Grazie!");

  return lines.join("\n");
}

function QtyControl({ item, price, qtyByVariant, onQtyChange, priceIndex }) {
  if (!price) {
    return null;
  }

  const variantKey = getVariantKey(item, priceIndex);
  const qty = sanitizeQty(qtyByVariant[variantKey]);
  const variantName = getVariantName(item, price);

  return (
    <div className="menu-price-order-cell">
      <span className="menu-price-value">{formatEuro(price.amount_cents)}</span>
      <div className="menu-qty-stepper" role="group" aria-label={`Quantita ${variantName}`}>
        <button
          type="button"
          className="menu-qty-button"
          aria-label={`Diminuisci ${variantName}`}
          onClick={() => onQtyChange(variantKey, qty - 1)}
        >
          -
        </button>
        <span className="menu-qty-value">{qty}</span>
        <button
          type="button"
          className="menu-qty-button"
          aria-label={`Aumenta ${variantName}`}
          onClick={() => onQtyChange(variantKey, qty + 1)}
        >
          +
        </button>
      </div>
    </div>
  );
}

function MenuTable({ items, sectionConfig, qtyByVariant, onQtyChange }) {
  const { hasDualPrices, thirdHeader, fourthHeader } = resolvePriceHeaders(items, sectionConfig);
  const tableClass = hasDualPrices ? "menu-table has-dual-prices" : "menu-table has-single-price";

  return (
    <div className="menu-items">
      <table className={tableClass}>
        <thead>
          <tr>
            <th>{sectionConfig.item_column_label}</th>
            {hasDualPrices ? (
              <>
                <th>{thirdHeader}</th>
                <th>{fourthHeader}</th>
              </>
            ) : (
              <th>{fourthHeader}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const firstPrice = item.prices[0];
            const secondPrice = item.prices[1];

            return (
              <tr key={item.id || `${item.section_key}-${item.subsection_key || "default"}-${item.name}`}>
                <td className="menu-item-cell">
                  <span className={item.section_key === "PIZZE" ? "pizza-name" : "item-name"}>{item.name}</span>
                  {item.description && <span className="menu-item-detail">{item.description}</span>}
                </td>
                {hasDualPrices ? (
                  <>
                    <td className="menu-price-column">
                      <QtyControl
                        item={item}
                        price={firstPrice}
                        priceIndex={0}
                        qtyByVariant={qtyByVariant}
                        onQtyChange={onQtyChange}
                      />
                    </td>
                    <td className="menu-price-column">
                      <QtyControl
                        item={item}
                        price={secondPrice}
                        priceIndex={1}
                        qtyByVariant={qtyByVariant}
                        onQtyChange={onQtyChange}
                      />
                    </td>
                  </>
                ) : (
                  <td className="menu-price-column">
                    <QtyControl
                      item={item}
                      price={firstPrice}
                      priceIndex={0}
                      qtyByVariant={qtyByVariant}
                      onQtyChange={onQtyChange}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const Menu = () => {
  const [items, setItems] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [qtyByVariant, setQtyByVariant] = useState({});
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [isStatusError, setIsStatusError] = useState(false);
  const [copyStatusMessage, setCopyStatusMessage] = useState("");

  useEffect(() => {
    let mounted = true;

    const loadMenu = async () => {
      try {
        const payload = await apiGet("/api/pizzeria-menu");
        if (!mounted) {
          return;
        }

        const normalizedItems = Array.isArray(payload?.items)
          ? payload.items.map(normalizeMenuItem).filter(Boolean)
          : [];

        setItems(sortPizzeriaMenuItems(normalizedItems));
      } catch (error) {
        console.warn("Unable to load pizzeria menu from API. Using static fallback.", error);
      } finally {
        if (mounted) {
          setIsLoaded(true);
        }
      }
    };

    loadMenu();

    return () => {
      mounted = false;
    };
  }, []);

  const sectionsWithItems = useMemo(
    () =>
      PIZZERIA_MENU_STRUCTURE.map((sectionConfig) => ({
        ...sectionConfig,
        sectionItems: items.filter((item) => item.active && item.section_key === sectionConfig.section_key)
      })).filter((sectionConfig) => sectionConfig.sectionItems.length > 0),
    [items]
  );

  const selectedEntries = useMemo(() => {
    const entries = [];

    sectionsWithItems.forEach((section) => {
      section.sectionItems.forEach((item) => {
        item.prices.forEach((price, priceIndex) => {
          const variantKey = getVariantKey(item, priceIndex);
          const qty = sanitizeQty(qtyByVariant[variantKey]);
          if (qty <= 0) {
            return;
          }

          entries.push({
            variant_key: variantKey,
            item,
            price,
            qty,
            line_total_cents: qty * price.amount_cents
          });
        });
      });
    });

    return entries;
  }, [sectionsWithItems, qtyByVariant]);

  const totalSelectedQty = useMemo(
    () => selectedEntries.reduce((total, entry) => total + entry.qty, 0),
    [selectedEntries]
  );
  const totalSelectedCents = useMemo(
    () => selectedEntries.reduce((total, entry) => total + entry.line_total_cents, 0),
    [selectedEntries]
  );
  const manualOrderText = useMemo(
    () => (totalSelectedQty > 0 ? buildWhatsappMessage(selectedEntries) : ""),
    [selectedEntries, totalSelectedQty]
  );

  const resetTransientMessages = () => {
    setStatusMessage("");
    setIsStatusError(false);
    setCopyStatusMessage("");
  };

  const handleQtyChange = (variantKey, nextQty) => {
    setQtyByVariant((current) => ({
      ...current,
      [variantKey]: sanitizeQty(nextQty)
    }));
    resetTransientMessages();
  };

  const handleResetOrder = () => {
    setQtyByVariant({});
    resetTransientMessages();
    setIsSummaryOpen(false);
  };

  const handleSendOrder = () => {
    setCopyStatusMessage("");

    if (totalSelectedQty <= 0) {
      setStatusMessage("Seleziona almeno un articolo prima di inviare.");
      setIsStatusError(true);
      return;
    }

    const normalizedPhone = normalizePhoneNumber(WHATSAPP_PHONE_NUMBER);
    const whatsappUrl = `https://wa.me/${normalizedPhone}?text=${encodeURIComponent(manualOrderText)}`;

    try {
      const popup = window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      if (!popup) {
        setStatusMessage("WhatsApp non si e aperto. Copia il testo ordine e invialo manualmente.");
        setIsStatusError(true);
        setIsSummaryOpen(true);
        return;
      }

      setStatusMessage("Ordine inviato, attendi conferma orario su WhatsApp.");
      setIsStatusError(false);
    } catch {
      setStatusMessage("WhatsApp non disponibile. Copia il testo ordine e invialo manualmente.");
      setIsStatusError(true);
      setIsSummaryOpen(true);
    }
  };

  const handleCopyOrderText = async () => {
    if (!manualOrderText) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(manualOrderText);
        setCopyStatusMessage("Testo ordine copiato.");
        return;
      }
    } catch {
      // fall through to textarea fallback
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = manualOrderText;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopyStatusMessage("Testo ordine copiato.");
    } catch {
      setCopyStatusMessage("Copia non riuscita. Seleziona e copia manualmente il testo.");
    }
  };

  if (!isLoaded || sectionsWithItems.length === 0) {
    return <MenuStaticContent />;
  }

  return (
    <div className="menu-container">
      <h1 className="menu-title">Il Nostro Menu</h1>
      <p className="menu-order-intro">
        Componi il tuo ordine e inviacelo su WhatsApp: ti confermeremo l&apos;orario in cui saranno pronte per il
        ritiro.
      </p>

      {sectionsWithItems.map((sectionConfig) => {
        if (sectionConfig.subsections.length > 0) {
          const subsectionBlocks = sectionConfig.subsections
            .map((subsection) => {
              const subsectionItems = sectionConfig.sectionItems.filter(
                (item) => item.subsection_key === subsection.key
              );
              if (subsectionItems.length === 0) {
                return null;
              }

              return (
                <div key={`${sectionConfig.section_key}-${subsection.key}`}>
                  <h3 className="subsection-title">{subsection.title}</h3>
                  <MenuTable
                    items={subsectionItems}
                    sectionConfig={sectionConfig}
                    qtyByVariant={qtyByVariant}
                    onQtyChange={handleQtyChange}
                  />
                </div>
              );
            })
            .filter(Boolean);

          if (subsectionBlocks.length === 0) {
            return null;
          }

          return (
            <section className="menu-section" key={sectionConfig.section_key}>
              <h2 className="section-title">{sectionConfig.title}</h2>
              {sectionConfig.description && <p className="section-description">{sectionConfig.description}</p>}
              {subsectionBlocks}
            </section>
          );
        }

        return (
          <section className="menu-section" key={sectionConfig.section_key}>
            <h2 className="section-title">{sectionConfig.title}</h2>
            <MenuTable
              items={sectionConfig.sectionItems}
              sectionConfig={sectionConfig}
              qtyByVariant={qtyByVariant}
              onQtyChange={handleQtyChange}
            />
          </section>
        );
      })}

      <section className="menu-notes">
        <h3>Note:</h3>
        <ul>
          <li>Ingredienti aggiuntivi da €0.50 a €2.00 a seconda degli ingredienti</li>
          <li>Tutte le pizze possono essere preparate con mozzarella di bufala (+€1.50)</li>
          <li>Si prega di informare il personale riguardo allergie o intolleranze</li>
        </ul>
      </section>

      <section className="menu-order-dock" aria-label="Ordine rapido menu">
        <div className="menu-order-dock-main">
          <button
            type="button"
            className="menu-order-summary-toggle"
            onClick={() => setIsSummaryOpen((current) => !current)}
          >
            {isSummaryOpen ? "Nascondi riepilogo" : "Mostra riepilogo"}
          </button>

          <div className="menu-order-totals">
            <span>{totalSelectedQty > 0 ? `Pezzi: ${totalSelectedQty}` : "Nessun articolo"}</span>
            <span>{formatEuro(totalSelectedCents)}</span>
          </div>

          <button type="button" className="menu-order-submit" onClick={handleSendOrder}>
            Ordina su WhatsApp
          </button>
        </div>

        {statusMessage && (
          <p className={`menu-order-status${isStatusError ? " is-error" : " is-success"}`}>{statusMessage}</p>
        )}

        {isSummaryOpen && (
          <div className="menu-order-expanded">
            {selectedEntries.length === 0 ? (
              <p className="menu-order-empty">Nessun articolo selezionato.</p>
            ) : (
              <ul className="menu-order-list">
                {selectedEntries.map((entry) => (
                  <li key={entry.variant_key}>
                    <span>{getVariantName(entry.item, entry.price)}</span>
                    <span>x{entry.qty}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="menu-order-expanded-actions">
              <button
                type="button"
                className="menu-order-copy"
                onClick={handleCopyOrderText}
                disabled={totalSelectedQty <= 0}
              >
                Copia testo ordine
              </button>
              <button type="button" className="menu-order-reset" onClick={handleResetOrder}>
                Azzera ordine
              </button>
            </div>

            {manualOrderText && <pre className="menu-order-manual-text">{manualOrderText}</pre>}
            {copyStatusMessage && <p className="menu-order-copy-status">{copyStatusMessage}</p>}
          </div>
        )}
      </section>
    </div>
  );
};

export default Menu;
